import type { Notification } from "../api/notifications";

/**
 * What a notification says (`docs/spec/notifications.md` §4). One sentence per
 * event type, written to read after the actor's name: "Ada Lovelace mentioned
 * you in".
 */
const SENTENCES: Readonly<Record<string, string>> = {
  "documents.publish": "published",
  "documents.update": "updated",
  "documents.add_user": "gave you access to",
  "documents.mentioned": "mentioned you in",
  "documents.group_mentioned": "mentioned your group in",
  "collections.create": "created a collection",
  "collections.add_user": "gave you access to a collection",
  "revisions.create": "edited",
  "comments.create": "commented on",
  "comments.mentioned": "mentioned you in a comment on",
  "comments.group_mentioned": "mentioned your group in a comment on",
  "comments.resolve": "resolved a thread on",
  "reactions.create": "reacted to your comment on",
  "emails.invite_accepted": "accepted your invitation",
  "emails.onboarding": "sent you a getting-started message",
  "emails.features": "announced a new feature",
  "emails.export_completed": "finished your export",
};

export function sentenceFor(notification: Notification): string {
  const base = SENTENCES[notification.event] ?? "sent you a notification about";
  if (notification.event !== "reactions.create") return base;
  const emoji = notification.data?.emoji;
  return typeof emoji === "string" && emoji !== "" ? `reacted ${emoji} to your comment on` : base;
}

export function actorName(notification: Notification): string {
  return notification.actor?.name ?? "Someone";
}

export function subjectTitle(notification: Notification): string | null {
  return notification.document?.title ?? null;
}

/**
 * Where clicking takes the reader (§9). A notification about a comment carries
 * the deep link of `comments.md` §15, with `resolved` when the thread it names
 * would otherwise be filtered out of the sidebar. A notification whose subject
 * is gone is not a link at all.
 */
export function targetFor(notification: Notification): string | null {
  const url = notification.document?.url;
  if (url === undefined || url === "") return null;
  if (notification.commentId === null) return url;
  const resolved = notification.comment?.resolvedAt != null;
  return `${url}?commentId=${encodeURIComponent(notification.commentId)}${
    resolved ? "&resolved=1" : ""
  }`;
}
